import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API_URL } from "../config/api";
import {
  Card,
  CardContent,
} from "../components/ui/card";
import {
  Search,
  Package,
  Users,
  Building2,
  Truck,
  FileText,
  Receipt,
  Scale,
  Target,
  ArrowRight,
  Loader2,
  Clock,
  Sparkles,
} from "lucide-react";


// Icon mapping
const ICON_MAP = {
  Package: Package,
  Users: Users,
  Building2: Building2,
  Truck: Truck,
  FileText: FileText,
  Receipt: Receipt,
  Scale: Scale,
  Target: Target,
};

// Type colors
const TYPE_COLORS = {
  order: "bg-blue-100 text-blue-700 border-blue-200",
  customer: "bg-green-100 text-green-700 border-green-200",
  vendor: "bg-purple-100 text-purple-700 border-purple-200",
  carrier: "bg-orange-100 text-orange-700 border-orange-200",
  invoice: "bg-red-100 text-red-700 border-red-200",
  quote: "bg-cyan-100 text-cyan-700 border-cyan-200",
  izvrsbe: "bg-yellow-100 text-yellow-700 border-yellow-200",
  lead: "bg-pink-100 text-pink-700 border-pink-200",
};

export default function GlobalSearchPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("recentSearches");
    if (stored) {
      setRecentSearches(JSON.parse(stored));
    }
    // Focus input on mount
    inputRef.current?.focus();
  }, []);

  // Save to recent searches
  const saveRecentSearch = (q) => {
    const updated = [q, ...recentSearches.filter(s => s !== q)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem("recentSearches", JSON.stringify(updated));
  };

  // Debounced search
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults(null);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/search/`, {
        params: { q: searchQuery, limit: 10 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResults(response.data);
      saveRecentSearch(searchQuery);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Get suggestions as user types
  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 1) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/search/suggestions`, {
        params: { q },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error("Suggestions failed:", error);
    }
  }, [token]);

  // Handle input change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.length >= 2) {
        performSearch(query);
      }
      fetchSuggestions(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, performSearch, fetchSuggestions]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && query.length >= 2) {
      performSearch(query);
      setShowSuggestions(false);
    }
    if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  // Navigate to item
  const handleItemClick = (link) => {
    navigate(link);
  };

  // Click on suggestion
  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.text);
    setShowSuggestions(false);
    if (suggestion.link) {
      navigate(suggestion.link);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Navbar />

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Search Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-lg shadow-blue-500/30">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Search Everything
          </h1>
          <p className="text-slate-400">
            Find customers, vendors, orders, invoices, and more
          </p>
        </div>

        {/* Search Input */}
        <div className="relative mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={handleKeyDown}
              placeholder="Search orders, customers, vendors, invoices..."
              className="w-full h-14 pl-12 pr-4 text-lg bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:bg-white/15 focus:border-blue-500 rounded-xl"
              data-testid="global-search-input"
            />
            {loading && (
              <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-blue-400 animate-spin" />
            )}
          </div>

          {/* Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute w-full mt-2 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden">
              {suggestions.map((suggestion, idx) => (
                <div
                  key={idx}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="px-4 py-3 hover:bg-slate-700 cursor-pointer flex items-center justify-between border-b border-slate-700/50 last:border-0"
                >
                  <span className="text-white">{suggestion.text}</span>
                  <Badge className={TYPE_COLORS[suggestion.type] || "bg-slate-600"}>
                    {suggestion.type}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Searches */}
        {!results && recentSearches.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Recent Searches
            </h3>
            <div className="flex flex-wrap gap-2">
              {recentSearches.map((search, idx) => (
                <button
                  key={idx}
                  onClick={() => setQuery(search)}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm transition-colors"
                >
                  {search}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quick Access Cards - filtered based on user type */}
        {!results && !query && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { icon: Package, label: "Orders", link: "/position-folders", color: "from-blue-500 to-blue-600", staffOnly: false },
              { icon: Users, label: "Customers", link: "/crm", color: "from-green-500 to-green-600", staffOnly: true },
              { icon: Building2, label: "Vendors", link: "/vendor-ap", color: "from-purple-500 to-purple-600", staffOnly: true },
              { icon: FileText, label: "Invoices", link: "/vendor-ap?tab=invoices", color: "from-red-500 to-red-600", staffOnly: true },
            ]
              .filter(item => !item.staffOnly || user?.is_staff || user?.is_admin || user?.user_type === 'staff')
              .map((item, idx) => (
              <Card
                key={idx}
                onClick={() => navigate(item.link)}
                className="bg-slate-800/50 border-slate-700 hover:bg-slate-700/50 cursor-pointer transition-all hover:scale-105"
              >
                <CardContent className="p-4 flex flex-col items-center gap-2">
                  <div className={`p-3 bg-gradient-to-br ${item.color} rounded-xl`}>
                    <item.icon className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-white font-medium">{item.label}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Search Results */}
        {results && (
          <div className="space-y-6">
            {/* Results Summary */}
            <div className="text-slate-400 text-sm">
              Found <span className="text-white font-bold">{results.total_count}</span> results for "{results.query}"
            </div>

            {/* Results by Category */}
            {results.categories.length === 0 ? (
              <div className="text-center py-12">
                <Search className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No results found for "{results.query}"</p>
                <p className="text-slate-500 text-sm mt-2">Try different keywords</p>
              </div>
            ) : (
              results.categories.map((category, catIdx) => {
                const IconComponent = ICON_MAP[category.icon] || Package;
                return (
                  <Card key={catIdx} className="bg-slate-800/50 border-slate-700 overflow-hidden">
                    <div className="px-4 py-3 bg-slate-700/50 flex items-center gap-3">
                      <IconComponent className="h-5 w-5 text-slate-300" />
                      <h3 className="font-semibold text-white">{category.name}</h3>
                      <Badge className="bg-slate-600 text-slate-200">{category.count}</Badge>
                    </div>
                    <CardContent className="p-0">
                      {category.items.map((item, itemIdx) => (
                        <div
                          key={itemIdx}
                          onClick={() => handleItemClick(item.link)}
                          className="px-4 py-3 hover:bg-slate-700/30 cursor-pointer flex items-center justify-between border-b border-slate-700/50 last:border-0 group"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-white truncate">{item.title}</div>
                            {item.subtitle && (
                              <div className="text-sm text-slate-400 truncate">{item.subtitle}</div>
                            )}
                          </div>
                          {item.extra && (
                            <Badge className={TYPE_COLORS[item.type] || "bg-slate-600"} variant="outline">
                              {item.extra}
                            </Badge>
                          )}
                          <ArrowRight className="h-4 w-4 text-slate-500 ml-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
